import { createSlice } from '@reduxjs/toolkit'
import {
	getOrCreateStreamChatUser,
	createStreamChatUser,
	createStreamChatToken,
	getStreamChatUserInfo,
	initializeStreamChat,
	getChatGroupsList,
	fetchChannelData,
	createChatGroup,
	getChatUsersList,
	getChatOperators,
	addGroupMember,
	deleteChatGroup,
	updateChatGroup,
	getUserSettings,
	updateUserSettings,
	getChannelMembersList,
	removeChannelMember,
	getChatGroupTypes,
	getUserPolicies,
	getSupportTypes,
	getAuthorizedSupportTypes,
	createSupportChat,
	getUsersSearch,
	createChannel,
	clearStreamChatState
} from '../actions/streamChatActions'

const initialState = {
	// Authentication token
	token: {
		data: null,
		isLoading: false,
		error: null,
		lastUpdated: null
	},
	
	// User information for Stream Chat
	userConfig: {
		data: null,
		isLoading: false,
		error: null
	},
	
	// Initialization state
	initialization: {
		isLoading: false,
		isInitialized: false,
		error: null
	},
	
	// Verification/getting Stream Chat user
	userVerification: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// Creation of Stream Chat user
	userCreation: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// Groups/Channels list
	groupsList: {
		isLoading: false,
		error: null,
		data: [],
		total: 0,
		lastUpdated: null
	},
	
	// Current active channel data
	activeChannel: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// Group/Channel creation
	groupCreation: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// Chat users list
	usersList: {
		isLoading: false,
		error: null,
		data: [],
		total: 0,
		lastUpdated: null
	},
	
	// Chat operators list
	operators: {
		isLoading: false,
		error: null,
		data: [],
		total: 0,
		lastUpdated: null
	},
	
	// Group membership management
	membershipManagement: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// Group deletion
	groupDeletion: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// Group update
	groupUpdate: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// User settings management
	userSettings: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// User settings update
	userSettingsUpdate: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// Channel members list. One slot, reused by whichever channel is open —
	// `group` records who the data currently belongs to so callers can tell a
	// warm cache from another channel's leftovers.
	channelMembersList: {
		isLoading: false,
		error: null,
		data: [],
		total: 0,
		group: null,
		canRemoveMembers: false,
		lastUpdated: null
	},
	
	// Channel member removal
	channelMemberRemoval: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// Chat group types
	groupTypes: {
		isLoading: false,
		error: null,
		data: [],
		total: 0,
		lastUpdated: null
	},
	
	// User policies/permissions
	userPolicies: {
		isLoading: false,
		error: null,
		policies: [],
		permissions: {},
		total: 0,
		lastUpdated: null
	},
	
	// Support types
	// Support types the signed-in operator may handle (Area Team console).
	authorizedSupportTypes: {
		data: [],
		isLoading: false,
		error: null
	},

	supportTypes: {
		isLoading: false,
		error: null,
		data: [],
		total: 0,
		lastUpdated: null
	},
	
	// Support chat creation
	supportChatCreation: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// Users search for chat creation
	usersSearch: {
		isLoading: false,
		error: null,
		data: [],
		hasMore: false,
		total: 0,
		lastUpdated: null
	},
	
	// Channel creation
	channelCreation: {
		isLoading: false,
		error: null,
		data: null
	},
	
	// General state
	isConnected: false,
	connectionError: null
}

const streamChatSlice = createSlice({
	name: 'streamChat',
	initialState,
	reducers: {
		// Reducer to update connection state
		setConnectionStatus: (state, action) => {
			state.isConnected = action.payload.isConnected
			state.connectionError = action.payload.error || null
		},
		
		// Reducer to clear errors
		clearErrors: (state) => {
			state.token.error = null
			state.userConfig.error = null
			state.initialization.error = null
			state.userVerification.error = null
			state.userCreation.error = null
			state.groupsList.error = null
			state.activeChannel.error = null
			state.groupCreation.error = null
			state.usersList.error = null
			state.membershipManagement.error = null
			state.groupDeletion.error = null
			state.userSettings.error = null
			state.userSettingsUpdate.error = null
			state.channelMembersList.error = null
			state.channelMemberRemoval.error = null
			state.groupTypes.error = null
			state.userPolicies.error = null
			state.connectionError = null
		},
		
		// Reducer to reset the state
		resetState: (state) => {
			return { ...initialState }
		}
	},
	extraReducers: (builder) => {
		// Verify/get Stream Chat user
		builder
			.addCase(getOrCreateStreamChatUser.pending, (state) => {
				state.userVerification.isLoading = true
				state.userVerification.error = null
			})
			.addCase(getOrCreateStreamChatUser.fulfilled, (state, action) => {
				state.userVerification.isLoading = false
				state.userVerification.data = action.payload
				state.userVerification.error = null
			})
			.addCase(getOrCreateStreamChatUser.rejected, (state, action) => {
				state.userVerification.isLoading = false
				state.userVerification.error = action.payload || action.error.message
				state.userVerification.data = null
			})

		// Create Stream Chat user
		builder
			.addCase(createStreamChatUser.pending, (state) => {
				state.userCreation.isLoading = true
				state.userCreation.error = null
			})
			.addCase(createStreamChatUser.fulfilled, (state, action) => {
				state.userCreation.isLoading = false
				state.userCreation.data = action.payload
				state.userCreation.error = null
			})
			.addCase(createStreamChatUser.rejected, (state, action) => {
				state.userCreation.isLoading = false
				state.userCreation.error = action.payload || action.error.message
				state.userCreation.data = null
			})

		// Create Stream Chat token
		builder
			.addCase(createStreamChatToken.pending, (state) => {
				state.token.isLoading = true
				state.token.error = null
			})
			.addCase(createStreamChatToken.fulfilled, (state, action) => {
				state.token.isLoading = false
				state.token.data = action.payload.token
				state.token.lastUpdated = action.payload.createdAt
				state.token.error = null
			})
			.addCase(createStreamChatToken.rejected, (state, action) => {
				state.token.isLoading = false
				state.token.error = action.payload || action.error.message
				state.token.data = null
			})

		// Get user information
		builder
			.addCase(getStreamChatUserInfo.pending, (state) => {
				state.userConfig.isLoading = true
				state.userConfig.error = null
			})
			.addCase(getStreamChatUserInfo.fulfilled, (state, action) => {
				state.userConfig.isLoading = false
				state.userConfig.data = action.payload
				state.userConfig.error = null
			})
			.addCase(getStreamChatUserInfo.rejected, (state, action) => {
				state.userConfig.isLoading = false
				state.userConfig.error = action.payload || action.error.message
				state.userConfig.data = null
			})

		// Initialize Stream Chat
		builder
			.addCase(initializeStreamChat.pending, (state) => {
				state.initialization.isLoading = true
				state.initialization.error = null
				state.initialization.isInitialized = false
			})
			.addCase(initializeStreamChat.fulfilled, (state, action) => {
				state.initialization.isLoading = false
				state.initialization.isInitialized = true
				state.initialization.error = null
				
				// Update related data
				state.token.data = action.payload.token
				state.userConfig.data = action.payload.userConfig
				state.isConnected = false // Will be updated when actually connected
			})
			.addCase(initializeStreamChat.rejected, (state, action) => {
				state.initialization.isLoading = false
				state.initialization.error = action.payload || action.error.message
				state.initialization.isInitialized = false
			})

		// Get chat groups list
		builder
			.addCase(getChatGroupsList.pending, (state) => {
				state.groupsList.isLoading = true
				state.groupsList.error = null
			})
			.addCase(getChatGroupsList.fulfilled, (state, action) => {
				state.groupsList.isLoading = false
				state.groupsList.data = action.payload.groups
				state.groupsList.total = action.payload.total
				state.groupsList.error = null
				state.groupsList.lastUpdated = new Date().toISOString()
			})
			.addCase(getChatGroupsList.rejected, (state, action) => {
				state.groupsList.isLoading = false
				state.groupsList.error = action.payload || action.error.message
			})

		// Fetch channel data
		builder
			.addCase(fetchChannelData.pending, (state) => {
				state.activeChannel.isLoading = true
				state.activeChannel.error = null
			})
			.addCase(fetchChannelData.fulfilled, (state, action) => {
				state.activeChannel.isLoading = false
				state.activeChannel.data = action.payload
				state.activeChannel.error = null
			})
			.addCase(fetchChannelData.rejected, (state, action) => {
				state.activeChannel.isLoading = false
				state.activeChannel.error = action.payload || action.error.message
				state.activeChannel.data = null
			})

		// Create chat group
		builder
			.addCase(createChatGroup.pending, (state) => {
				state.groupCreation.isLoading = true
				state.groupCreation.error = null
			})
			.addCase(createChatGroup.fulfilled, (state, action) => {
				state.groupCreation.isLoading = false
				state.groupCreation.data = action.payload
				state.groupCreation.error = null
				// Add the new group to the list if it exists
				if (state.groupsList.data) {
					state.groupsList.data.push({
						id: action.payload.id,
						providerGroupId: action.payload.providerGroupId,
						ownerChatUserId: action.payload.ownerChatUserId,
						name: action.payload.name,
						description: action.payload.description,
						imageUrl: action.payload.imageUrl,
						isPublic: action.payload.isPublic,
						isModerated: action.payload.isModerated,
						chatGroupTypeId: action.payload.chatGroupTypeId,
						chatProviderData: action.payload.chatProviderData
					})
					state.groupsList.total += 1
				}
			})
			.addCase(createChatGroup.rejected, (state, action) => {
				state.groupCreation.isLoading = false
				state.groupCreation.error = action.payload || action.error.message
				state.groupCreation.data = null
			})

		// Get chat users list
		builder
			.addCase(getChatUsersList.pending, (state) => {
				state.usersList.isLoading = true
				state.usersList.error = null
			})
			.addCase(getChatUsersList.fulfilled, (state, action) => {
				state.usersList.isLoading = false
				state.usersList.data = action.payload.users
				state.usersList.total = action.payload.total
				state.usersList.error = null
				state.usersList.lastUpdated = new Date().toISOString()
			})
			.addCase(getChatUsersList.rejected, (state, action) => {
				state.usersList.isLoading = false
				state.usersList.error = action.payload || action.error.message
			})

		// Get chat operators
		builder
			.addCase(getChatOperators.pending, (state) => {
				state.operators.isLoading = true
				state.operators.error = null
			})
			.addCase(getChatOperators.fulfilled, (state, action) => {
				state.operators.isLoading = false
				state.operators.data = action.payload.operators
				state.operators.total = action.payload.total
				state.operators.error = null
				state.operators.lastUpdated = new Date().toISOString()
			})
			.addCase(getChatOperators.rejected, (state, action) => {
				state.operators.isLoading = false
				state.operators.error = action.payload || action.error.message
			})

		// Add group member
		builder
			.addCase(addGroupMember.pending, (state) => {
				state.membershipManagement.isLoading = true
				state.membershipManagement.error = null
			})
			.addCase(addGroupMember.fulfilled, (state, action) => {
				state.membershipManagement.isLoading = false
				state.membershipManagement.data = action.payload
				state.membershipManagement.error = null
			})
			.addCase(addGroupMember.rejected, (state, action) => {
				state.membershipManagement.isLoading = false
				state.membershipManagement.error = action.payload || action.error.message
				state.membershipManagement.data = null
			})

		// Delete chat group
		builder
			.addCase(deleteChatGroup.pending, (state) => {
				state.groupDeletion.isLoading = true
				state.groupDeletion.error = null
			})
			.addCase(deleteChatGroup.fulfilled, (state, action) => {
				state.groupDeletion.isLoading = false
				state.groupDeletion.data = action.payload
				state.groupDeletion.error = null
				// Remove the deleted group from the list
				if (state.groupsList.data) {
					state.groupsList.data = state.groupsList.data.filter(
						group => group.providerGroupId !== action.payload.groupId
					)
					state.groupsList.total = Math.max(0, state.groupsList.total - 1)
				}
				
				// Also remove from channels list if it exists
				// This ensures immediate visual feedback
			})
			.addCase(deleteChatGroup.rejected, (state, action) => {
				state.groupDeletion.isLoading = false
				state.groupDeletion.error = action.payload || action.error.message
				state.groupDeletion.data = null
			})

		// Update chat group
		builder
			.addCase(updateChatGroup.pending, (state) => {
				state.groupUpdate.isLoading = true
				state.groupUpdate.error = null
			})
			.addCase(updateChatGroup.fulfilled, (state, action) => {
				state.groupUpdate.isLoading = false
				state.groupUpdate.data = action.payload
				state.groupUpdate.error = null
			})
			.addCase(updateChatGroup.rejected, (state, action) => {
				state.groupUpdate.isLoading = false
				state.groupUpdate.error = action.payload || action.error.message
				state.groupUpdate.data = null
			})

		// Get user settings
		builder
			.addCase(getUserSettings.pending, (state) => {
				state.userSettings.isLoading = true
				state.userSettings.error = null
			})
			.addCase(getUserSettings.fulfilled, (state, action) => {
				state.userSettings.isLoading = false
				state.userSettings.data = action.payload
				state.userSettings.error = null
			})
			.addCase(getUserSettings.rejected, (state, action) => {
				state.userSettings.isLoading = false
				state.userSettings.error = action.payload || action.error.message
				state.userSettings.data = null
			})

		// Update user settings
		builder
			.addCase(updateUserSettings.pending, (state) => {
				state.userSettingsUpdate.isLoading = true
				state.userSettingsUpdate.error = null
			})
			.addCase(updateUserSettings.fulfilled, (state, action) => {
				state.userSettingsUpdate.isLoading = false
				state.userSettingsUpdate.data = action.payload
				state.userSettingsUpdate.error = null
				// Update the user settings data with the new information
				if (state.userSettings.data) {
					state.userSettings.data.user = action.payload.user
				}
			})
			.addCase(updateUserSettings.rejected, (state, action) => {
				state.userSettingsUpdate.isLoading = false
				state.userSettingsUpdate.error = action.payload || action.error.message
				state.userSettingsUpdate.data = null
			})

		// Get channel members list
		builder
			.addCase(getChannelMembersList.pending, (state) => {
				state.channelMembersList.isLoading = true
				state.channelMembersList.error = null
			})
			.addCase(getChannelMembersList.fulfilled, (state, action) => {
				state.channelMembersList.isLoading = false
				state.channelMembersList.data = action.payload.members
				state.channelMembersList.total = action.payload.total
				state.channelMembersList.group = action.payload.group ?? null
				state.channelMembersList.canRemoveMembers = action.payload.canRemoveMembers === true
				state.channelMembersList.error = null
				state.channelMembersList.lastUpdated = new Date().toISOString()
			})
			.addCase(getChannelMembersList.rejected, (state, action) => {
				state.channelMembersList.isLoading = false
				state.channelMembersList.error = action.payload || action.error.message
			})

		// Remove channel member
		builder
			.addCase(removeChannelMember.pending, (state) => {
				state.channelMemberRemoval.isLoading = true
				state.channelMemberRemoval.error = null
			})
			.addCase(removeChannelMember.fulfilled, (state, action) => {
				state.channelMemberRemoval.isLoading = false
				state.channelMemberRemoval.data = action.payload
				state.channelMemberRemoval.error = null
				// Remove the member from the list
				if (state.channelMembersList.data) {
					state.channelMembersList.data = state.channelMembersList.data.filter(
						member => member.userId !== action.payload.userId
					)
					state.channelMembersList.total = Math.max(0, state.channelMembersList.total - 1)
				}
			})
			.addCase(removeChannelMember.rejected, (state, action) => {
				state.channelMemberRemoval.isLoading = false
				state.channelMemberRemoval.error = action.payload || action.error.message
				state.channelMemberRemoval.data = null
			})

		// Get chat group types
		builder
			.addCase(getChatGroupTypes.pending, (state) => {
				state.groupTypes.isLoading = true
				state.groupTypes.error = null
			})
			.addCase(getChatGroupTypes.fulfilled, (state, action) => {
				state.groupTypes.isLoading = false
				state.groupTypes.data = action.payload.groupTypes // Changed from types to groupTypes
				state.groupTypes.total = action.payload.total
				state.groupTypes.error = null
				state.groupTypes.lastUpdated = new Date().toISOString()
			})
			.addCase(getChatGroupTypes.rejected, (state, action) => {
				state.groupTypes.isLoading = false
				state.groupTypes.error = action.payload || action.error.message
			})

		// Get user policies
		builder
			.addCase(getUserPolicies.pending, (state) => {
				state.userPolicies.isLoading = true
				state.userPolicies.error = null
			})
			.addCase(getUserPolicies.fulfilled, (state, action) => {
				state.userPolicies.isLoading = false
				state.userPolicies.policies = action.payload.policies
				state.userPolicies.permissions = action.payload.permissions
				state.userPolicies.total = action.payload.total
				state.userPolicies.error = null
				state.userPolicies.lastUpdated = new Date().toISOString()
			})
			.addCase(getUserPolicies.rejected, (state, action) => {
				state.userPolicies.isLoading = false
				state.userPolicies.error = action.payload || action.error.message
			})

		// Support types
		builder
			.addCase(getSupportTypes.pending, (state) => {
				state.supportTypes.isLoading = true
				state.supportTypes.error = null
			})
			.addCase(getSupportTypes.fulfilled, (state, action) => {
				state.supportTypes.isLoading = false
				state.supportTypes.data = action.payload.supportTypes || []
				state.supportTypes.total = action.payload.supportTypes?.length || 0
				state.supportTypes.error = null
				state.supportTypes.lastUpdated = new Date().toISOString()
			})
			.addCase(getSupportTypes.rejected, (state, action) => {
				state.supportTypes.isLoading = false
				state.supportTypes.error = action.payload || action.error.message
			})
			.addCase(getAuthorizedSupportTypes.pending, (state) => {
				state.authorizedSupportTypes.isLoading = true
				state.authorizedSupportTypes.error = null
			})
			.addCase(getAuthorizedSupportTypes.fulfilled, (state, action) => {
				state.authorizedSupportTypes.isLoading = false
				state.authorizedSupportTypes.data = action.payload || []
				state.authorizedSupportTypes.error = null
			})
			.addCase(getAuthorizedSupportTypes.rejected, (state, action) => {
				state.authorizedSupportTypes.isLoading = false
				state.authorizedSupportTypes.error = action.payload || action.error.message
			})

		// Support chat creation
		builder
			.addCase(createSupportChat.pending, (state) => {
				state.supportChatCreation.isLoading = true
				state.supportChatCreation.error = null
			})
			.addCase(createSupportChat.fulfilled, (state, action) => {
				state.supportChatCreation.isLoading = false
				state.supportChatCreation.data = action.payload
				state.supportChatCreation.error = null
			})
			.addCase(createSupportChat.rejected, (state, action) => {
				state.supportChatCreation.isLoading = false
				state.supportChatCreation.error = action.payload || action.error.message
			})

		// Users search
		builder
			.addCase(getUsersSearch.pending, (state) => {
				state.usersSearch.isLoading = true
				state.usersSearch.error = null
			})
			.addCase(getUsersSearch.fulfilled, (state, action) => {
				state.usersSearch.isLoading = false
				state.usersSearch.data = action.payload.users
				state.usersSearch.hasMore = action.payload.hasMore
				state.usersSearch.total = action.payload.total
				state.usersSearch.error = null
				state.usersSearch.lastUpdated = new Date().toISOString()
			})
			.addCase(getUsersSearch.rejected, (state, action) => {
				state.usersSearch.isLoading = false
				state.usersSearch.error = action.payload || action.error.message
			})

		// Channel creation
		builder
			.addCase(createChannel.pending, (state) => {
				state.channelCreation.isLoading = true
				state.channelCreation.error = null
			})
			.addCase(createChannel.fulfilled, (state, action) => {
				state.channelCreation.isLoading = false
				state.channelCreation.data = action.payload
				state.channelCreation.error = null
			})
			.addCase(createChannel.rejected, (state, action) => {
				state.channelCreation.isLoading = false
				state.channelCreation.error = action.payload || action.error.message
			})

		// Clear state
		builder
			.addCase(clearStreamChatState.fulfilled, (state, action) => {
				return { ...initialState }
			})
	}
})

export const { 
	setConnectionStatus, 
	clearErrors, 
	resetState 
} = streamChatSlice.actions

/**
 * Conversations across all of the operator's support types that are still
 * waiting for a team reply — drives the Area Team menu badge.
 */
export const selectSupportAwaitingTotal = (state) =>
	(state.streamChat?.authorizedSupportTypes?.data || []).reduce(
		(total, type) => total + (Number(type.awaitingReplyCount) || 0),
		0
	)

export default streamChatSlice.reducer


